import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import get from 'lodash/get';
import pickBy from 'lodash/pickBy';
import PropTypes from 'prop-types';
import { useAppState } from 'AppProvider';
import { isExperienceMultipleTiers, parseTextFromHtmlString } from 'utils';
import toZonedIsoDateTime from 'utils/date/toZonedIsoDateTime';
import {
  STATUS_FAILURE,
  STATUS_IDLE,
  STATUS_PENDING,
  STATUS_SUCCESS,
} from 'types/app';

const SCHEMA_URL = 'https://schema.org';

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}/;

const isSettled = (status) =>
  status === STATUS_SUCCESS || status === STATUS_FAILURE;

/**
 * An availability entry is a `YYYY-MM-DD` string for bookable experiences and a
 * `{ date, sessions }` object for open ones (the 'dates-with-sessions' endpoint).
 */
const toDateString = (value) => {
  const date = typeof value === 'string' ? value : value?.date;
  return typeof date === 'string' && DATE_PATTERN.test(date)
    ? date.substring(0, 10)
    : null;
};

const SchemaMarkup = ({ url, image, brandName, brandUrl }) => {
  const {
    experience,
    sessions: availableSessions,
    sessionsSelectedDate,
    firstAvailableDate,
    fetchFirstAvailabilityStatus,
    searchAvailabilityStatus,
    fetchSessionStatus,
    availabilities,
  } = useAppState((state) => state.experience);
  const isMultipleTiers = isExperienceMultipleTiers(experience);
  const experienceCoverImage = get(experience, 'coverPicture.uri.original');
  const firstBookableDate = useMemo(
    () => toDateString(firstAvailableDate ?? availabilities[0]),
    [firstAvailableDate, availabilities],
  );

  // The start is the first loaded session, on the date those sessions were fetched for — the
  // first available date normally, or the `?sessionDate=` of a deep link — with the venue's UTC
  // offset on that date (DST-aware). No valid date or time → no startDate (a Product, below).
  const { startDateTime, endDateTime } = useMemo(() => {
    const date =
      toDateString(availableSessions.length ? sessionsSelectedDate : null) ??
      firstBookableDate;
    const time = availableSessions[0]?.startTime;
    if (!date || !time) {
      return { startDateTime: null, endDateTime: null };
    }
    const start = `${date}T${time}`;
    const timezoneId = get(experience, 'location.timezoneId');
    const startIso = toZonedIsoDateTime(start, timezoneId);
    return {
      startDateTime: startIso,
      endDateTime:
        startIso &&
        toZonedIsoDateTime(
          start,
          timezoneId,
          Number(experience?.sessionDurationMinutes) || 0,
        ),
    };
  }, [availableSessions, sessionsSelectedDate, firstBookableDate, experience]);
  const isDateValid = Boolean(startDateTime);

  // The page loads in a chain — experience → first available date (or a deep link's
  // `?sessionDate=`) → that date's sessions — and the start time and per-tier seats come from the
  // sessions. "No sessions" mid-load only means "not loaded yet", so nothing is emitted while a
  // request is in flight: a crawler snapshot must not record an Event without a startDate, or a
  // bookable experience as SoldOut. IDLE means a request was never made (a deep link skips the
  // first available date), not that one is pending — so only PENDING is waited on, plus the
  // sessions a known first available date is about to request.
  const statuses = [
    fetchFirstAvailabilityStatus,
    searchAvailabilityStatus,
    fetchSessionStatus,
  ];
  const hasStarted = statuses.some((status) => status !== STATUS_IDLE);
  const isInFlight = statuses.some((status) => status === STATUS_PENDING);
  const isAwaitingSessions =
    Boolean(firstAvailableDate) && !isSettled(fetchSessionStatus);
  const isLoading = !hasStarted || isInFlight || isAwaitingSessions;

  const schemaData = useMemo(() => {
    if (isLoading) {
      return null;
    }

    const hostFirstName = get(experience, 'hostedBy.firstName');
    const hostLastName = get(experience, 'hostedBy.lastName');
    const hasNamedHost =
      !experience?.isNoHost && Boolean(hostFirstName || hostLastName);
    // A real host → Person; otherwise fall back to the brand as an Organization so
    // the Event never emits a `performer` of "undefined undefined" (or none at all).
    const performer = hasNamedHost
      ? {
          '@type': 'Person',
          name: [hostFirstName, hostLastName].filter(Boolean).join(' '),
        }
      : { '@type': 'Organization', name: brandName, url: brandUrl };

    // PostalAddress from whatever location parts exist (no street/postal in the
    // data) — omit the whole address when none are present rather than emit blanks.
    const addressParts = pickBy(
      {
        streetAddress: get(experience, 'location.additionalInfo'),
        addressLocality: get(experience, 'location.city'),
        addressRegion: get(experience, 'location.state'),
        addressCountry: get(experience, 'location.country'),
      },
      Boolean,
    );
    const hasAddress = Object.keys(addressParts).length > 0;

    // Seats come from the loaded sessions: a tier is SoldOut only when every loaded session is
    // full for it. With no sessions loaded, it follows whether a bookable date exists.
    const tierAvailability = (tier) => {
      if (!availableSessions.length) {
        return firstBookableDate ? 'InStock' : 'SoldOut';
      }
      const hasSeats = availableSessions.some(
        (sess) =>
          sess.supportedParticipantsCount > 0 &&
          sess.availableQuantityPerTier?.[tier.name] > 0,
      );
      return hasSeats ? 'InStock' : 'SoldOut';
    };

    const offers = experience?.priceTiers?.map((tier) => ({
      '@type': 'Offer',
      name: tier.name,
      price: tier.price,
      priceCurrency: experience?.currency,
      ...(isDateValid && { validFrom: startDateTime }),
      url,
      availability: `${SCHEMA_URL}/${tierAvailability(tier)}`,
    }));
    const offersData = experience?.hidePrice
      ? undefined
      : (isMultipleTiers ? offers : offers?.[0]) || undefined;

    const common = {
      '@context': SCHEMA_URL,
      url,
      name: experience?.title,
      description: parseTextFromHtmlString(
        experience?.description || experience?.title || '',
      ),
      image: image || experienceCoverImage,
      ...(offersData && { offers: offersData }),
    };

    // Google requires `startDate` on an Event. Without a bookable date the experience is
    // described as a Product (price + availability) rather than an invalid Event.
    if (!isDateValid) {
      return {
        ...common,
        '@type': 'Product',
        brand: { '@type': 'Organization', name: brandName, url: brandUrl },
      };
    }

    return {
      ...common,
      '@type': 'Event',
      startDate: startDateTime,
      endDate: endDateTime,
      eventStatus: `${SCHEMA_URL}/EventScheduled`,
      eventAttendanceMode: `${SCHEMA_URL}/OfflineEventAttendanceMode`,
      location: {
        '@type': 'Place',
        name: get(experience, 'location.name'),
        ...(hasAddress && {
          address: { '@type': 'PostalAddress', ...addressParts },
        }),
        geo: {
          '@type': 'GeoCoordinates',
          latitude: get(experience, 'location.latitude'),
          longitude: get(experience, 'location.longitude'),
        },
      },
      organizer: { '@type': 'Organization', name: brandName, url: brandUrl },
      performer,
    };
  }, [
    experience,
    availableSessions,
    firstBookableDate,
    startDateTime,
    endDateTime,
    isLoading,
    isDateValid,
    image,
    url,
    brandName,
    brandUrl,
  ]);

  if (!schemaData) {
    return null;
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
    </Helmet>
  );
};

SchemaMarkup.propTypes = {
  url: PropTypes.string.isRequired,
  image: PropTypes.string,
  brandName: PropTypes.string,
  brandUrl: PropTypes.string,
};

SchemaMarkup.defaultProps = {
  image: undefined,
  brandName: undefined,
  brandUrl: undefined,
};

export default React.memo(SchemaMarkup);
